import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, Sparkles, CheckCircle2, Zap, Terminal, Cpu,
  Phone, Users, TrendingUp, FileText,
  DollarSign, ClipboardCheck, Shield,
  Lock, RefreshCw, Globe, Layers, BarChart3, Network,
  MessageSquare, Clock, Stethoscope,
  ChevronRight, Check, X, Activity, Database, GitBranch,
  Mic, Brain, Workflow, Radio, BoxSelect, Play,
} from 'lucide-react';
import Footer from '../components/Footer';

/* ─────────────────────────────────────────────────────────────────────────
   SHARED: clean light header (white, subtle border on scroll, blue accents)
   ───────────────────────────────────────────────────────────────────────── */

const FuturisticHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled
        ? 'bg-white/85 backdrop-blur-xl border-b border-slate-200/70 shadow-[0_1px_0_rgba(15,23,42,0.04)]'
        : 'bg-transparent border-b border-transparent'
    }`}>
      <nav className="max-w-7xl mx-auto px-6 h-[68px] flex items-center justify-between">
        <Link to="/alt2" className="flex items-center gap-2.5 group">
          <div className="relative">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 flex items-center justify-center shadow-md shadow-blue-600/25">
              <span className="text-white font-bold text-sm">A</span>
            </div>
            <div className="absolute -inset-1 bg-gradient-to-br from-blue-400/40 to-indigo-500/30 rounded-lg blur-md group-hover:opacity-100 opacity-60 transition-opacity -z-10" />
          </div>
          <span className="font-semibold text-base text-slate-900 tracking-tight">Asha</span>
          <span className="hidden sm:inline-flex items-center gap-1 ml-1 px-1.5 py-0.5 rounded-md bg-blue-50 border border-blue-100 text-[10px] font-bold text-blue-700 uppercase tracking-wider">AI OS</span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {[
            { label: 'Platform', href: '#platform' },
            { label: 'AI Workforce', href: '#workforce' },
            { label: 'ARIA', href: '#aria' },
            { label: 'Deploy', href: '#deploy' },
            { label: 'Pricing', href: '#pricing' },
          ].map(item => (
            <a
              key={item.label}
              href={item.href}
              className="px-3 py-2 text-[13.5px] font-medium text-slate-600 hover:text-slate-900 transition-colors"
            >
              {item.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <Link to="/book-demo" className="hidden sm:inline-flex text-[13.5px] font-medium text-slate-600 hover:text-slate-900 px-3 py-2 transition-colors">
            Sign in
          </Link>
          <Link
            to="/book-demo"
            className="group relative inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 text-white text-[13.5px] font-semibold hover:bg-blue-700 transition-all shadow-sm shadow-blue-600/20"
          >
            Book a demo
            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
      </nav>
    </header>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   HERO — light command-deck for the AI OS
   ───────────────────────────────────────────────────────────────────────── */

const heroPrompts = [
  { text: 'Answer the phones while we are closed', agent: 'Amy', role: 'Receptionist', img: '/assets/amy.png', stack: ['transcribe.wav', 'pms.lookup(patient)', 'calendar.book()', 'sms.confirm()'] },
  { text: 'Run verifications for tomorrow\'s schedule', agent: 'Amanda', role: 'Verification', img: '/assets/amanda.png', stack: ['payer.dial()', 'breakdown.parse()', 'pms.write(coverage)', 'flag.copays()'] },
  { text: 'Scrub today\'s claims before submission', agent: 'Alayna', role: 'Claim Scrubbing', img: 'https://framerusercontent.com/images/gpvxkPXtZHSDq7LbDrvpyEwKzAk.png', stack: ['claim.validate()', 'cdt.lint()', 'attachment.check()', 'clearinghouse.submit()'] },
  { text: 'Post every EOB and reconcile payments', agent: 'Alisha', role: 'EOB Posting', img: '/assets/alisha.png', stack: ['era.parse(835)', 'ledger.post()', 'underpayment.flag()', 'denial.route()'] },
  { text: 'Work this week\'s denials and file appeals', agent: 'Sarah', role: 'Denial Management', img: '/assets/sarah.png', stack: ['eob.read()', 'policy.lookup()', 'appeal.draft()', 'fax.submit()'] },
  { text: 'Fill open hygiene slots for next week', agent: 'Stephanie', role: 'Hygiene Recall', img: '/assets/stephanie.png', stack: ['recall.queue()', 'patient.voice_call()', 'chart.context()', 'pms.book()'] },
];

const HeroFuturistic = () => {
  const [idx, setIdx] = useState(0);
  const [text, setText] = useState('');
  const [phase, setPhase] = useState('typing');
  const [feed, setFeed] = useState([]);

  useEffect(() => {
    const p = heroPrompts[idx];
    let i = 0;
    let t1, t2;
    if (phase === 'typing') {
      setText('');
      t1 = setInterval(() => {
        i++; setText(p.text.slice(0, i));
        if (i >= p.text.length) { clearInterval(t1); t2 = setTimeout(() => setPhase('routing'), 350); }
      }, 32);
    } else if (phase === 'routing') {
      t2 = setTimeout(() => setPhase('running'), 900);
    } else if (phase === 'running') {
      setFeed(prev => [{ id: Date.now(), ...p, t: new Date() }, ...prev].slice(0, 5));
      t2 = setTimeout(() => setPhase('done'), 1500);
    } else if (phase === 'done') {
      t2 = setTimeout(() => setPhase('pause'), 1100);
    } else if (phase === 'pause') {
      t2 = setTimeout(() => { setIdx(v => (v + 1) % heroPrompts.length); setPhase('typing'); }, 400);
    }
    return () => { clearInterval(t1); clearTimeout(t2); };
  }, [idx, phase]);

  const cur = heroPrompts[idx];

  return (
    <section className="relative overflow-hidden bg-white pt-[68px]">
      {/* Animated background grid */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage:
              'linear-gradient(rgba(37, 99, 235, 0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(37, 99, 235, 0.06) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
            maskImage: 'radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 80%)',
          }}
        />
        <div className="absolute top-[-200px] left-1/2 -translate-x-1/2 w-[1100px] h-[700px] bg-blue-200/50 rounded-full blur-[140px]" />
        <div className="absolute top-[10%] right-[-100px] w-[500px] h-[500px] bg-indigo-200/40 rounded-full blur-[120px]" />
        <div className="absolute top-[20%] left-[-100px] w-[500px] h-[500px] bg-sky-200/40 rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-20 pb-24">
        {/* Tagline pill */}
        <motion.div
          initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
          className="flex justify-center mb-8"
        >
          <a href="#aria" className="group inline-flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full bg-white border border-slate-200 backdrop-blur-sm hover:border-blue-200 hover:shadow-sm transition-all">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-bold uppercase tracking-wider border border-blue-100">
              <span className="w-1 h-1 rounded-full bg-blue-500 animate-pulse" />
              New
            </span>
            <span className="text-[13px] font-medium text-slate-700">
              ARIA-2 · the world's first dental reasoning model
            </span>
            <ChevronRight className="w-3 h-3 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
          </a>
        </motion.div>

        {/* Headline */}
        <div className="text-center max-w-5xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }}
            className="text-[44px] md:text-[64px] lg:text-[80px] font-semibold leading-[0.98] tracking-[-0.035em] text-slate-900"
          >
            The AI Operating System
            <br />
            <span className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 bg-clip-text text-transparent">
              for dental practices.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.14 }}
            className="mt-7 text-[17px] md:text-[19px] text-slate-600 leading-[1.5] max-w-2xl mx-auto"
          >
            20+ AI employees — dispatched by a dental-trained reasoning model. Layer
            onto Dentrix, Eaglesoft, or Open Dental — or migrate to the world's first
            AI-native PMS.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22 }}
            className="mt-10 flex flex-wrap items-center justify-center gap-3"
          >
            <Link
              to="/book-demo"
              className="group relative inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-blue-600 text-white font-semibold text-[14.5px] hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/25"
            >
              Book a demo
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <a
              href="#platform"
              className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border border-slate-200 text-slate-900 font-semibold text-[14.5px] hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm"
            >
              <Play className="w-3 h-3 fill-current" />
              See the system
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.32 }}
            className="mt-6 flex items-center justify-center gap-5 text-[13px] text-slate-500"
          >
            <span className="inline-flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> HIPAA · SOC2</span>
            <span className="w-1 h-1 rounded-full bg-slate-300" />
            <span>4-week go-live</span>
            <span className="w-1 h-1 rounded-full bg-slate-300" />
            <span>Net-positive month 1</span>
          </motion.div>
        </div>

        {/* Command Deck */}
        <motion.div
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.7 }}
          className="mt-16 max-w-5xl mx-auto"
        >
          <div className="relative">
            {/* Glow underlay */}
            <div className="absolute -inset-px bg-gradient-to-b from-blue-300/40 via-indigo-300/20 to-transparent rounded-2xl blur-md" />
            <div className="relative rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.18)]">
              {/* Title bar */}
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-slate-50/70">
                <div className="flex items-center gap-2.5">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                    <div className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                    <div className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                  </div>
                  <span className="text-[12.5px] font-semibold text-slate-700">Asha · Workforce</span>
                </div>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-[11.5px] font-medium text-emerald-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
                </div>
              </div>

              {/* Body */}
              <div className="p-5 md:p-6">
                {/* Command bar */}
                <div className={`rounded-xl border overflow-hidden transition-all duration-500 ${
                  phase === 'typing'
                    ? 'border-blue-300 shadow-[0_0_30px_-10px_rgba(37,99,235,0.4)] bg-blue-50/40'
                    : phase === 'routing'
                    ? 'border-indigo-300 shadow-[0_0_30px_-10px_rgba(99,102,241,0.4)] bg-indigo-50/40'
                    : phase === 'running' || phase === 'done'
                    ? 'border-emerald-300 shadow-[0_0_30px_-10px_rgba(16,185,129,0.4)] bg-emerald-50/40'
                    : 'border-slate-200'
                }`}>
                  <div className="flex items-center gap-3 px-4 py-3.5 bg-white">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                      phase === 'typing' ? 'bg-gradient-to-br from-blue-500 to-blue-600' :
                      phase === 'routing' ? 'bg-gradient-to-br from-indigo-500 to-indigo-600' :
                      'bg-gradient-to-br from-emerald-500 to-teal-500'
                    }`}>
                      {phase === 'typing' ? <Sparkles className="w-4 h-4 text-white" /> :
                       phase === 'routing' ? <Network className="w-4 h-4 text-white" /> :
                       <Cpu className="w-4 h-4 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-semibold text-slate-500 mb-0.5">
                        {phase === 'typing' ? 'You ask' :
                         phase === 'routing' ? 'ARIA routes the task' :
                         'Your AI agent executes'}
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-[14.5px] font-medium text-slate-900 truncate">{text}</span>
                        {phase === 'typing' && (
                          <motion.span animate={{ opacity: [1, 0] }} transition={{ duration: 0.5, repeat: Infinity }} className="w-[2px] h-4 bg-blue-600 inline-block" />
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-50 border border-blue-100">
                        <Cpu className="w-3 h-3 text-blue-600" />
                        <span className="text-[11.5px] font-semibold text-blue-700">ARIA</span>
                      </div>
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-white transition-colors ${
                        phase === 'typing' ? 'bg-blue-600' :
                        phase === 'routing' ? 'bg-indigo-600' : 'bg-emerald-500'
                      }`}>
                        {phase === 'typing' ? <ArrowRight className="w-4 h-4" /> :
                         phase === 'routing' ? <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}><Workflow className="w-4 h-4" /></motion.div> :
                         <CheckCircle2 className="w-4 h-4" />}
                      </div>
                    </div>
                  </div>

                  {/* Routing trace */}
                  <AnimatePresence>
                    {(phase === 'routing' || phase === 'running' || phase === 'done') && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50/60">
                          <div className="flex items-center gap-3">
                            <img src={cur.img} alt={cur.agent} className="w-7 h-7 rounded-lg object-cover ring-1 ring-slate-200" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1.5">
                                <span className="text-[13.5px] font-semibold text-slate-900">{cur.agent}</span>
                                <span className="text-slate-300">·</span>
                                <span className="text-[12px] text-slate-500">{cur.role}</span>
                                {phase === 'done' && (
                                  <span className="ml-1 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600">
                                    <CheckCircle2 className="w-3 h-3" /> completed in 1.2s
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-1.5">
                                {cur.stack.map((s, i) => (
                                  <motion.span
                                    key={s}
                                    initial={{ opacity: 0, x: -4 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: i * 0.18 }}
                                    className={`text-[10.5px] font-mono px-1.5 py-0.5 rounded ${
                                      phase === 'done'
                                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                                        : 'bg-white text-slate-600 border border-slate-200'
                                    }`}
                                  >
                                    {s}
                                  </motion.span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Two-pane: activity feed + system stats */}
                <div className="mt-5 grid md:grid-cols-12 gap-4">
                  {/* Live feed */}
                  <div className="md:col-span-7">
                    <div className="rounded-xl border border-slate-200 bg-slate-50/60 overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200">
                        <div className="inline-flex items-center gap-2">
                          <Radio className="w-3.5 h-3.5 text-blue-600" />
                          <span className="text-[12.5px] font-semibold text-slate-700">Activity</span>
                        </div>
                        <span className="text-[11.5px] text-emerald-700 inline-flex items-center gap-1.5 font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Autonomous
                        </span>
                      </div>
                      <div className="p-2 space-y-1 min-h-[220px]">
                        <AnimatePresence mode="popLayout">
                          {feed.map(task => (
                            <motion.div
                              key={task.id}
                              layout
                              initial={{ opacity: 0, x: -10, scale: 0.97 }}
                              animate={{ opacity: 1, x: 0, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.97 }}
                              className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white border border-slate-200"
                            >
                              <img src={task.img} alt={task.agent} className="w-6 h-6 rounded-md object-cover ring-1 ring-slate-200" />
                              <div className="flex-1 min-w-0 text-[12px] truncate">
                                <span className="font-semibold text-slate-900">{task.agent}</span>
                                <span className="text-slate-500 ml-1">
                                  {task.role === 'Receptionist' ? '· answered 7 patient calls' :
                                   task.role === 'Claim Scrubbing' ? '· scrubbed 14 claims (0 denials predicted)' :
                                   task.role === 'Verification' ? '· verified 9 patients · 2 alerts surfaced' :
                                   task.role === 'EOB Posting' ? '· posted 22 EOBs · $8,420 reconciled' :
                                   task.role === 'Hygiene Recall' ? '· booked 6 hygiene appts for next week' :
                                   '· filed 4 appeals with clinical evidence'}
                                </span>
                              </div>
                              <span className="text-[10.5px] tabular-nums text-slate-400">
                                {task.t.toLocaleTimeString().slice(0, 5)}
                              </span>
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                            </motion.div>
                          ))}
                        </AnimatePresence>
                        {feed.length === 0 && (
                          <div className="flex flex-col items-center justify-center h-[220px] text-slate-400 text-[12px]">
                            <Brain className="w-6 h-6 mb-2" />
                            Waiting for instructions
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* System telemetry */}
                  <div className="md:col-span-5">
                    <div className="rounded-xl border border-slate-200 bg-slate-50/60 overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200">
                        <div className="inline-flex items-center gap-2">
                          <Activity className="w-3.5 h-3.5 text-indigo-600" />
                          <span className="text-[12.5px] font-semibold text-slate-700">Today</span>
                        </div>
                        <span className="text-[11.5px] text-slate-400">Live</span>
                      </div>
                      <div className="p-3 space-y-2.5">
                        {[
                          { l: 'Tasks executed', v: '847', delta: '+12%', glow: 'blue', icon: CheckCircle2 },
                          { l: 'Calls handled', v: '124', delta: '+8%', glow: 'sky', icon: Phone },
                          { l: 'Claims processed', v: '63', delta: '+22%', glow: 'indigo', icon: FileText },
                          { l: 'Revenue captured', v: '$34.2K', delta: '+18%', glow: 'emerald', icon: TrendingUp },
                        ].map(s => {
                          const I = s.icon;
                          const dot = s.glow === 'blue' ? 'bg-blue-500' : s.glow === 'sky' ? 'bg-sky-500' : s.glow === 'indigo' ? 'bg-indigo-500' : 'bg-emerald-500';
                          return (
                            <div key={s.l} className="flex items-center gap-3 px-2.5 py-2 rounded-lg bg-white border border-slate-200">
                              <div className={`w-7 h-7 rounded-md flex items-center justify-center bg-slate-100`}>
                                <I className="w-3.5 h-3.5 text-slate-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-[11.5px] text-slate-500">{s.l}</div>
                                <div className="text-[15px] font-bold text-slate-900 tabular-nums">{s.v}</div>
                              </div>
                              <div className="text-[11px] text-emerald-600 inline-flex items-center gap-1 font-medium">
                                <span className={`w-1 h-1 rounded-full ${dot}`} /> {s.delta}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Logo / trust strip */}
        <motion.div
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          className="mt-20 max-w-5xl mx-auto"
        >
          <div className="text-center text-[13px] text-slate-500 mb-5">
            Trusted by practices across the country
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-x-6 gap-y-4 items-center">
            {['Pacific Dental', 'Harbor Smiles', 'Northside DDS', 'Bluebird Dental', 'Oakwood Family', 'Summit Dental'].map(n => (
              <div key={n} className="text-center text-[13px] font-semibold tracking-tight text-slate-500">{n}</div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   AGENT WORKFORCE — card grid with tab switcher (AgentsSection layout)
   ───────────────────────────────────────────────────────────────────────── */

const frontOfficeAgents = [
  { n: 'Amy', r: 'Receptionist', img: '/assets/amy.png', link: '/agents/receptionist', desc: 'Answers every inbound call 24/7. Trained on 7,300+ dental calls. Books directly into your PMS.', skills: ['Inbound Calls', 'Scheduling', 'Q&A'] },
  { n: 'Leena', r: 'Schedule Optimizer', img: '/assets/leena.png', link: '/agents/schedule-optimizer', desc: '12-step workflow to fill open chairs and prevent no-shows. +28% scheduled production.', skills: ['Schedule Filling', 'Confirmation', 'Recall'] },
  { n: 'Stephanie', r: 'Hygiene Recall', img: '/assets/stephanie.png', link: '/agents/hygiene-recall', desc: 'Voice campaigns with chart-aware context. Books into the right operatory for the right provider.', skills: ['Outbound Calls', 'Reactivation', 'Booking'] },
  { n: 'Kim', r: 'Case Acceptance', img: '/assets/kim.png', link: '/agents/case-acceptance', desc: 'Unlimited AI follow-ups on proposed treatment plans. Lifts case acceptance by 34%.', skills: ['Treatment Pres.', 'Education', 'Financing'] },
  { n: 'Morgan', r: 'Patient Intake', img: '/assets/morgan.png', link: '/agents/patient-intake', desc: 'Forms, insurance cards, and medical history — completed before patients arrive.', skills: ['Forms', 'Insurance', 'Intake'] },
  { n: 'Elise', r: 'Patient Experience', img: '/assets/elise.png', link: '/agents/patient-experience', desc: 'Review generation, post-visit follow-ups, and NPS tracking. 4.9★ practice average.', skills: ['Follow-ups', 'Reviews', 'NPS'] },
];

const backOfficeAgents = [
  { n: 'Amanda', r: 'Verification', img: '/assets/amanda.png', link: '/agents/insurance-verification', desc: 'Procedure-level breakdowns. Catches coverage issues days in advance — written into your PMS.', skills: ['Verification', 'Benefits', 'Eligibility'] },
  { n: 'Laura', r: 'Insurance Calls', img: 'https://framerusercontent.com/images/8s85NGEzWU5LVKALaMlopNKSHg.png', link: '/agents/insurance-calls', desc: 'AI phone calls to payers. Navigates IVRs and speaks to representatives.', skills: ['Outbound', 'IVR Nav', 'Status'] },
  { n: 'Ariel', r: 'Claim Submission', img: '/assets/ariel.png', link: '/agents/claim-submission', desc: 'Auto-submits clean claims to the clearinghouse and tracks every status update.', skills: ['Claims', 'Submission', 'Coding'] },
  { n: 'Alayna', r: 'Claim Scrubbing', img: 'https://framerusercontent.com/images/gpvxkPXtZHSDq7LbDrvpyEwKzAk.png', link: '/agents/claim-scrubbing', desc: 'Scrubs every claim pre-submission. 97% clean-claim rate across active practices.', skills: ['Scrubbing', 'Validation', 'CDT'] },
  { n: 'Sarah', r: 'Denial Mgmt', img: '/assets/sarah.png', link: '/agents/denial-management', desc: 'Multi-step reasoning across 16+ data points. One-tap auto-appeal generation.', skills: ['Appeals', 'Resubmission', 'Negotiation'] },
  { n: 'Kianna', r: 'Patient Billing', img: '/assets/kianna.png', link: '/agents/patient-billing', desc: 'Voice and text billing campaigns. Gentle, persistent, effective — your team stays out of it.', skills: ['Statements', 'Payments', 'Inquiries'] },
];

const AgentNetworkSection = () => {
  const [tab, setTab] = useState('front');
  const agents = tab === 'front' ? frontOfficeAgents : backOfficeAgents;

  return (
    <section id="platform" className="relative bg-slate-50/60 py-28 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-blue-200/40 rounded-full blur-[140px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-[12px] font-semibold mb-5">
              <Sparkles className="w-3 h-3" /> Your AI workforce
            </div>
            <h2 className="text-[32px] md:text-[44px] font-semibold tracking-[-0.025em] leading-[1.08] text-slate-900">
              The only platform with a <span className="text-blue-600">full team of 12+ dental AI agents</span> for every use case
            </h2>
            <p className="mt-5 text-[15.5px] md:text-[16.5px] text-slate-600 leading-relaxed">
              Every agent reasons on <span className="text-slate-900 font-semibold">ARIA</span> — our dental-trained model. They share context, hand off tasks, and run in parallel. It's not 20 chatbots. It's one workforce.
            </p>
          </div>

          {/* Tab Switcher */}
          <div className="bg-white border border-slate-200 p-1 rounded-full inline-flex flex-shrink-0 relative shadow-sm">
            <motion.div
              className="absolute top-1 bottom-1 rounded-full bg-blue-600 shadow-sm z-0"
              initial={false}
              animate={{ left: tab === 'front' ? '4px' : '50%', width: 'calc(50% - 6px)', x: tab === 'front' ? 0 : 2 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            />
            {[{ id: 'front', label: 'Front Office' }, { id: 'back', label: 'Back Office' }].map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-6 py-2.5 rounded-full text-[13.5px] font-semibold transition-colors duration-200 relative z-10 ${
                  tab === t.id ? 'text-white' : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Agent grid */}
        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {agents.map(agent => {
              const CardInner = () => (
                <>
                  <div className="p-6 pb-0 flex items-start justify-between">
                    <div className="relative">
                      <div className="w-24 h-24 rounded-2xl overflow-hidden ring-1 ring-slate-100">
                        {agent.img ? (
                          <img src={agent.img} alt={agent.n} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-700 text-white text-3xl font-bold">
                            {agent.n[0]}
                          </div>
                        )}
                      </div>
                      <div className="absolute -bottom-1 -right-1 bg-emerald-500 border-[3px] border-white w-6 h-6 rounded-full" />
                    </div>
                    {agent.link && (
                      <div className="w-8 h-8 rounded-full bg-slate-50 group-hover:bg-blue-600 flex items-center justify-center transition-all duration-300">
                        <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-white transition-colors" />
                      </div>
                    )}
                  </div>
                  <div className="p-6 pt-5 flex-1 flex flex-col">
                    <div className="mb-3">
                      <h3 className="text-[19px] font-bold text-slate-900 mb-0.5">{agent.n}</h3>
                      <p className="text-[12px] font-semibold text-blue-600 uppercase tracking-wider">{agent.r}</p>
                    </div>
                    <p className="text-slate-500 text-[13.5px] leading-relaxed mb-4 flex-1">{agent.desc}</p>
                    <div className="flex flex-wrap gap-1.5 mt-auto">
                      {agent.skills?.map((skill, i) => (
                        <span key={i} className="px-2.5 py-1 rounded-full bg-slate-50 text-slate-500 text-[10.5px] font-semibold border border-slate-100 uppercase tracking-wider">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </>
              );

              return (
                <motion.div
                  key={agent.n + agent.r}
                  layout
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.22 }}
                >
                  {agent.link ? (
                    <Link to={agent.link} className="group relative bg-white rounded-2xl border border-slate-200/80 hover:border-slate-300 hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col h-full">
                      <CardInner />
                    </Link>
                  ) : (
                    <div className="group relative bg-white rounded-2xl border border-slate-200/80 hover:border-slate-300 hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col h-full">
                      <CardInner />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   TWO PATHS — Layer on your PMS or migrate to Asha PMS
   ───────────────────────────────────────────────────────────────────────── */

const TwoPathsSection = () => {
  const [active, setActive] = useState('layer');

  return (
    <section id="deploy" className="relative bg-white py-28 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-0 w-[600px] h-[600px] bg-blue-100/50 rounded-full blur-[140px]" />
        <div className="absolute top-1/2 right-0 w-[600px] h-[600px] bg-indigo-100/50 rounded-full blur-[140px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-[10.5px] font-semibold uppercase tracking-wider mb-5">
            <GitBranch className="w-3 h-3" /> deployment paths
          </div>
          <h2 className="text-[34px] md:text-[48px] font-semibold tracking-[-0.025em] leading-[1.05] text-slate-900">
            Two ways to deploy.
            <br />
            <span className="text-slate-400">Zero compromise on AI.</span>
          </h2>
          <p className="mt-5 text-[16.5px] text-slate-600 leading-relaxed">
            Keep your current PMS and deploy our AI workforce on top of it. Or migrate to the world's first AI-native dental PMS for the fully unified experience. You choose.
          </p>
        </div>

        {/* Toggle */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex items-center p-1 rounded-full bg-slate-100 border border-slate-200 backdrop-blur-sm">
            {[
              { id: 'layer', label: 'Layer on your PMS', sub: 'fast' },
              { id: 'migrate', label: 'Migrate to Asha PMS', sub: 'unified' },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setActive(t.id)}
                className={`relative inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13.5px] font-semibold transition-all ${
                  active === t.id
                    ? 'text-white'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {active === t.id && (
                  <motion.div
                    layoutId="deployment-pill"
                    className="absolute inset-0 bg-blue-600 rounded-full shadow-md shadow-blue-600/30"
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative">{t.label}</span>
                <span className={`relative text-[11px] font-medium ${active === t.id ? 'text-white/70' : 'text-slate-400'}`}>{t.sub}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Path content */}
        <AnimatePresence mode="wait">
          {active === 'layer' ? (
            <motion.div
              key="layer"
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3 }}
              className="grid lg:grid-cols-12 gap-8 items-stretch"
            >
              <div className="lg:col-span-5">
                <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-blue-50 border border-blue-100 text-blue-700 text-[10px] font-semibold uppercase tracking-wider mb-4">
                  path 01 · layer
                </div>
                <h3 className="text-[28px] md:text-[34px] font-semibold tracking-[-0.02em] leading-[1.1] text-slate-900">
                  Keep your PMS.
                  <br />
                  Add the AI workforce.
                </h3>
                <p className="mt-4 text-[15.5px] text-slate-600 leading-relaxed">
                  Don't rip and replace. Our AI agents connect into Dentrix, Eaglesoft, or Open Dental — and start working on day one. Verifications, calls, claims, A/R — handled in your existing system.
                </p>
                <div className="mt-7 space-y-2.5">
                  {[
                    { i: Zap, t: 'Go-live in 2–3 weeks', s: 'No data migration. No retraining.' },
                    { i: Lock, t: 'Read/write to your PMS', s: 'Direct connectors — not screen scraping.' },
                    { i: Workflow, t: 'Same agents. Same ARIA model.', s: 'Full workforce, layered on top.' },
                  ].map((r, i) => {
                    const I = r.i;
                    return (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
                          <I className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <div className="text-[14px] font-semibold text-slate-900">{r.t}</div>
                          <div className="text-[12.5px] text-slate-500">{r.s}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <Link to="/integrations" className="mt-7 inline-flex items-center gap-1.5 text-[14px] font-semibold text-blue-600 hover:text-blue-700 group">
                  See supported PMS integrations
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>

              <div className="lg:col-span-7">
                <div className="relative h-full">
                  <div className="absolute -inset-px bg-gradient-to-br from-blue-300/40 to-indigo-300/30 rounded-2xl blur opacity-60" />
                  <div className="relative h-full rounded-2xl border border-slate-200 bg-white shadow-md p-6 overflow-hidden">
                    {/* Visual: Asha layer floats above PMS */}
                    <div className="space-y-3">
                      <div className="rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 p-4 relative overflow-hidden">
                        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(59,130,246,0.15) 0%, transparent 50%)' }} />
                        <div className="relative flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md shadow-blue-600/30">
                              <Brain className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <div className="text-[10px] font-semibold uppercase tracking-wider text-blue-700">asha ai layer</div>
                              <div className="text-[14px] font-semibold text-slate-900">ARIA · 20+ AI employees · Comms fabric</div>
                            </div>
                          </div>
                          <span className="text-[11px] font-medium px-2 py-1 rounded-full bg-white text-blue-700 border border-blue-100 inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />Live</span>
                        </div>
                      </div>

                      {/* Connector with packets */}
                      <div className="flex justify-center py-1 relative">
                        <div className="w-px h-12 bg-gradient-to-b from-blue-400/60 to-slate-200 relative">
                          {[0, 1, 2].map(i => (
                            <motion.div
                              key={i}
                              animate={{ y: [0, 48], opacity: [1, 0] }}
                              transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.5, ease: 'linear' }}
                              className="absolute -left-[3px] w-1.5 h-1.5 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.6)]"
                            />
                          ))}
                        </div>
                      </div>

                      {/* PMS layer with logos */}
                      <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center shadow-sm">
                            <Database className="w-5 h-5 text-slate-500" />
                          </div>
                          <div>
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">your existing pms</div>
                            <div className="text-[14px] font-semibold text-slate-900">Asha bridges into your system of record</div>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { n: 'Dentrix', logo: '/assets/dental-software-logos/pms/dentrix.png' },
                            { n: 'Eaglesoft', logo: '/assets/dental-software-logos/pms/eaglesoft.png' },
                            { n: 'Open Dental', logo: '/assets/dental-software-logos/pms/opendental.png' },
                          ].map(p => (
                            <div key={p.n} className="rounded-lg border border-slate-200 bg-white px-3 py-3 flex flex-col items-center gap-1.5 shadow-sm">
                              <div className="h-7 flex items-center">
                                <img src={p.logo} alt={p.n} className="max-h-7 max-w-[60px] object-contain opacity-90" onError={(e) => { e.target.style.display = 'none'; }} />
                              </div>
                              <div className="text-[11px] font-semibold text-slate-700">{p.n}</div>
                              <div className="text-[10.5px] text-emerald-600 inline-flex items-center gap-1 font-medium">
                                <span className="w-1 h-1 rounded-full bg-emerald-500" /> Connected
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="mt-3 text-[12px] text-slate-400 text-center">+ Curve, Carestack, Denticon, Practice-Web, SoftDent…</div>
                      </div>

                      {/* Data flow trace */}
                      <div className="mt-2 rounded-xl border border-slate-200 bg-slate-900 px-4 py-3 font-mono text-[11px] space-y-1">
                        <div className="text-slate-500">{'// real-time bidirectional sync'}</div>
                        <div className="text-slate-200"><span className="text-blue-400">amanda.verify</span>(<span className="text-amber-300">"John Doe"</span>) <span className="text-slate-500">→</span> <span className="text-emerald-400">pms.write(coverage, copay, deductible)</span></div>
                        <div className="text-slate-200"><span className="text-blue-400">amy.book</span>(<span className="text-amber-300">"cleaning"</span>) <span className="text-slate-500">→</span> <span className="text-emerald-400">pms.appointments.create()</span></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="migrate"
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3 }}
              className="grid lg:grid-cols-12 gap-8 items-stretch"
            >
              <div className="lg:col-span-5">
                <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-indigo-50 border border-indigo-100 text-indigo-700 text-[10px] font-semibold uppercase tracking-wider mb-4">
                  path 02 · migrate
                </div>
                <h3 className="text-[28px] md:text-[34px] font-semibold tracking-[-0.02em] leading-[1.1] text-slate-900">
                  Migrate to Asha PMS.
                  <br />
                  Unify everything.
                </h3>
                <p className="mt-4 text-[15.5px] text-slate-600 leading-relaxed">
                  When you're ready, migrate to the world's first AI-native dental PMS. Charts, perio, treatment planning, scheduling, ledgers, imaging — all in one cloud platform. Plus AI Copilot, AI Scribe, AI Treatment Planner built right in.
                </p>
                <div className="mt-7 space-y-2.5">
                  {[
                    { i: Layers, t: 'Replace 10+ softwares', s: 'PMS + VoIP + verification + claims + imaging + more.' },
                    { i: Stethoscope, t: 'Clinical AI built in', s: 'AI Scribe · AI Treatment Planner · AI Copilot.' },
                    { i: Shield, t: 'White-glove 4-week go-live', s: 'Full migration, QA, training. Zero downtime.' },
                  ].map((r, i) => {
                    const I = r.i;
                    return (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0">
                          <I className="w-4 h-4 text-indigo-600" />
                        </div>
                        <div>
                          <div className="text-[14px] font-semibold text-slate-900">{r.t}</div>
                          <div className="text-[12.5px] text-slate-500">{r.s}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <Link to="/integrations" className="mt-7 inline-flex items-center gap-1.5 text-[14px] font-semibold text-indigo-600 hover:text-indigo-700 group">
                  Plan your migration
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>

              <div className="lg:col-span-7">
                <div className="relative h-full">
                  <div className="absolute -inset-px bg-gradient-to-br from-indigo-300/40 to-blue-300/30 rounded-2xl blur opacity-60" />
                  <div className="relative h-full rounded-2xl border border-slate-200 bg-white shadow-md p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-indigo-700">unified asha os</div>
                      <span className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">10 softwares · 1 login</span>
                    </div>

                    {/* Replaced-by-Asha grid */}
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {[
                        { o: 'Dentrix', icon: Database },
                        { o: 'Weave', icon: Phone },
                        { o: 'eAssist', icon: FileText },
                        { o: 'Dentina', icon: MessageSquare },
                        { o: 'Vyne', icon: ClipboardCheck },
                        { o: 'Pearl AI', icon: BoxSelect },
                      ].map((p, i) => (
                        <motion.div
                          key={p.o}
                          initial={{ opacity: 0, scale: 0.95 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: i * 0.04 }}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50/70 relative overflow-hidden"
                        >
                          <p.icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="text-[12.5px] text-slate-500 line-through decoration-rose-400/60 truncate">{p.o}</span>
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 ml-auto shrink-0" />
                        </motion.div>
                      ))}
                    </div>

                    {/* Asha unified card */}
                    <div className="rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-blue-50 to-white p-4 relative overflow-hidden">
                      <div className="absolute inset-0 opacity-40" style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, rgba(99,102,241,0.18) 0%, transparent 50%)' }} />
                      <div className="relative">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-md shadow-indigo-600/30">
                            <Sparkles className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-indigo-700">asha ai-native pms</div>
                            <div className="text-[14px] font-semibold text-slate-900">Single platform · Unified record · 20+ AI</div>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-1.5">
                          {['Charts', 'Perio', 'Tx Planning', 'Scheduling', 'Ledgers', 'Imaging', 'AI Copilot', 'AI Scribe', 'Tx Planner'].map((f, i) => (
                            <div key={f} className={`text-[11.5px] font-medium px-2 py-1.5 rounded text-center ${
                              i >= 6 ? 'bg-indigo-100 text-indigo-700 border border-indigo-200' : 'bg-white text-slate-600 border border-slate-200'
                            }`}>{f}</div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 text-[12.5px] text-center text-slate-500">
                      <span className="text-emerald-600 font-semibold">$799</span>/mo + 3.5% collections · all included
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   WORKFORCE — full agent grid with spotlight
   ───────────────────────────────────────────────────────────────────────── */

const agentsList = [
  { name: 'Amy', role: 'AI Receptionist', img: '/assets/amy.png', desc: 'Answers every patient call, 24/7. Books directly into your PMS. Trained on 7,300+ dental calls.', tag: 'voice · multilingual', team: 'front' },
  { name: 'Leena', role: 'Schedule Optimizer', img: null, desc: '12-step intelligent workflow to fill open chairs and prevent no-shows.', tag: '+28% production', team: 'front' },
  { name: 'Stephanie', role: 'Hygiene Recall', img: '/assets/stephanie.png', desc: 'Voice campaigns with chart-aware context. Books into the right operatory.', tag: 'natural voice', team: 'front' },
  { name: 'Kim', role: 'Case Acceptance', img: '/assets/kim.png', desc: 'Unlimited AI follow-ups on proposed treatment plans. +34% case acceptance.', tag: 'lifts acceptance', team: 'front' },
  { name: 'Morgan', role: 'Patient Intake', img: null, desc: 'Forms, insurance cards, medical history — completed before patient arrival.', tag: 'zero-touch intake', team: 'front' },
  { name: 'Elise', role: 'Patient Experience', img: '/assets/elise.png', desc: 'Review generation, post-visit follow-ups. 4.9★ average.', tag: 'reviews engine', team: 'front' },
  { name: 'Trisha', role: 'Practice Manager', img: null, desc: 'AI chief of staff. Task dashboard, ops oversight, morning triage.', tag: 'morning huddle', team: 'front' },
  { name: 'Joseph', role: 'Fax Manager', img: '/assets/joseph.png', desc: 'Intelligent fax triage and routing. No more paper stacks.', tag: 'intelligent routing', team: 'front' },
  { name: 'Amanda', role: 'Verification', img: '/assets/amanda.png', desc: 'Procedure-level breakdowns. Catches coverage issues days in advance.', tag: 'procedure-level', team: 'back' },
  { name: 'Laura', role: 'Insurance Calls', img: 'https://framerusercontent.com/images/8s85NGEzWU5LVKALaMlopNKSHg.png', desc: 'AI phone calls to payers. Navigates IVRs and speaks to reps.', tag: 'IVR navigation', team: 'back' },
  { name: 'Ariel', role: 'Claim Submission', img: '/assets/ariel.png', desc: 'Auto-submits claims to the clearinghouse. Tracks every status.', tag: 'auto-submit', team: 'back' },
  { name: 'Alayna', role: 'Claim Scrubbing', img: 'https://framerusercontent.com/images/gpvxkPXtZHSDq7LbDrvpyEwKzAk.png', desc: 'Scrubs claims pre-submission. 97% clean-claim rate.', tag: '97% clean', team: 'back' },
  { name: 'Sarah', role: 'Denial Management', img: '/assets/sarah.png', desc: 'Multi-step reasoning across 16+ data points. 1-tap auto-appeal generation.', tag: 'self-challenging', team: 'back' },
  { name: 'Kianna', role: 'Patient Billing', img: '/assets/kianna.png', desc: 'Voice + text billing campaigns. Gentle, persistent, effective.', tag: 'voice + sms', team: 'back' },
  { name: 'Julia', role: 'A/R Collections', img: '/assets/julia.png', desc: 'Recovers aged receivables through multi-channel outreach.', tag: 'multi-channel', team: 'back' },
  { name: 'Alisha', role: 'EOB Posting', img: '/assets/alisha.png', desc: 'Posts ERAs and EOBs overnight. Your A/R starts each day at zero.', tag: 'overnight posting', team: 'back' },
];

const WorkforceSection = () => {
  const [active, setActive] = useState(0);
  const [filter, setFilter] = useState('all');

  const filtered = agentsList.map((a, i) => ({ ...a, originalIdx: i })).filter(a => filter === 'all' || a.team === filter);

  return (
    <section id="workforce" className="relative bg-slate-50/60 py-28 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage:
              'linear-gradient(rgba(37, 99, 235, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(37, 99, 235, 0.05) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-[10.5px] font-semibold uppercase tracking-wider mb-5">
            <Users className="w-3 h-3" /> ai workforce
          </div>
          <h2 className="text-[34px] md:text-[48px] font-semibold tracking-[-0.025em] leading-[1.05] text-slate-900">
            16+ specialized AI employees.
            <br />
            <span className="text-slate-400">Ready on day one.</span>
          </h2>
          <p className="mt-5 text-[16.5px] text-slate-600 leading-relaxed">
            Every agent shares state through ARIA. Amanda's verification is what Sarah uses for denials, what Ariel uses for submissions, what Amy uses on calls. That coordination is the entire point.
          </p>
        </div>

        {/* Filter */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white border border-slate-200 shadow-sm">
            {[
              { id: 'all', label: 'All 16 agents' },
              { id: 'front', label: 'Front office' },
              { id: 'back', label: 'Revenue cycle' },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => { setFilter(t.id); setActive(0); }}
                className={`relative px-4 py-1.5 rounded-full text-[12.5px] font-semibold transition-colors ${
                  filter === t.id ? 'text-white' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {filter === t.id && <motion.div layoutId="workforce-pill" className="absolute inset-0 bg-blue-600 rounded-full shadow-sm shadow-blue-600/30" transition={{ type: 'spring', stiffness: 400, damping: 30 }} />}
                <span className="relative">{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Spotlight + grid */}
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Spotlight */}
          <div className="lg:col-span-5">
            <div className="sticky top-24">
              <div className="relative rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-md p-6">
                <div className="absolute -top-20 -right-20 w-72 h-72 bg-blue-200/40 rounded-full blur-[80px] pointer-events-none" />
                <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-indigo-200/40 rounded-full blur-[80px] pointer-events-none" />

                <AnimatePresence mode="wait">
                  <motion.div
                    key={filtered[active]?.name}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="relative"
                  >
                    <div className="flex items-center gap-4 mb-5">
                      <div className="relative shrink-0">
                        {filtered[active]?.img ? (
                          <img src={filtered[active].img} alt={filtered[active].name} className="w-20 h-20 rounded-2xl object-cover ring-2 ring-slate-200" />
                        ) : (
                          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-2xl font-bold ring-2 ring-slate-200">
                            {filtered[active]?.name[0]}
                          </div>
                        )}
                        <div className="absolute -bottom-1.5 -right-1.5 w-7 h-7 rounded-xl bg-emerald-500 flex items-center justify-center ring-2 ring-white shadow-md shadow-emerald-500/40">
                          <Sparkles className="w-3.5 h-3.5 text-white" />
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-blue-700">{filtered[active]?.tag}</div>
                        <div className="text-[24px] font-semibold text-slate-900 leading-tight">{filtered[active]?.name}</div>
                        <div className="text-[13px] text-slate-500">{filtered[active]?.role}</div>
                      </div>
                    </div>

                    <p className="text-[14.5px] text-slate-600 leading-relaxed mb-5">
                      {filtered[active]?.desc}
                    </p>

                    {/* code-style sample */}
                    <div className="rounded-lg border border-slate-200 bg-slate-900 px-3 py-2.5 font-mono text-[11.5px] mb-5">
                      <div className="text-slate-500">{'// dispatched by ARIA'}</div>
                      <div>
                        <span className="text-blue-400">{filtered[active]?.name.toLowerCase()}</span>
                        <span className="text-slate-400">.</span>
                        <span className="text-indigo-300">execute</span>
                        <span className="text-slate-400">(</span>
                        <span className="text-amber-300">"{filtered[active]?.role.toLowerCase()}"</span>
                        <span className="text-slate-400">)</span>
                      </div>
                    </div>

                    <Link to="/book-demo" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-blue-600 hover:text-blue-700 group">
                      See {filtered[active]?.name} in your practice
                      <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Agent grid */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {filtered.map((a, i) => (
                <button
                  key={a.name}
                  onClick={() => setActive(i)}
                  className={`relative text-left rounded-xl border transition-all p-3 ${
                    active === i
                      ? 'bg-white border-blue-300 shadow-md shadow-blue-600/15'
                      : 'bg-white border-slate-200 hover:border-blue-200 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    {a.img ? (
                      <img src={a.img} alt={a.name} className="w-9 h-9 rounded-lg object-cover ring-1 ring-slate-200 shrink-0" />
                    ) : (
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold shrink-0">
                        {a.name[0]}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="text-[12.5px] font-semibold text-slate-900 truncate leading-tight">{a.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{a.role}</div>
                    </div>
                  </div>
                  {active === i && (
                    <motion.div
                      layoutId="active-agent-marker"
                      className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.6)]"
                    />
                  )}
                </button>
              ))}
            </div>

            {/* Footnote stats */}
            <div className="mt-5 grid grid-cols-3 gap-2.5">
              {[
                { v: '4', l: 'clinical AI products' },
                { v: '20+', l: 'total AI employees' },
                { v: '24/7', l: 'autonomous operation' },
              ].map(s => (
                <div key={s.l} className="rounded-xl border border-slate-200 bg-white px-3 py-3 text-center shadow-sm">
                  <div className="text-[18px] font-bold text-slate-900 tabular-nums">{s.v}</div>
                  <div className="text-[11.5px] text-slate-500">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   ROI / STATS strip
   ───────────────────────────────────────────────────────────────────────── */

const StatsBar = () => {
  const stats = [
    { v: '$9.8K', l: 'avg monthly revenue lift', s: 'across active practices' },
    { v: '+34%', l: 'case acceptance', s: 'AI Treatment Planner' },
    { v: '+28%', l: 'scheduled production', s: 'Leena schedule optimizer' },
    { v: '~3 hrs', l: 'staff time / patient', s: 'manual work automated' },
  ];
  return (
    <section className="relative bg-white pb-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-px bg-slate-200 rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
          {stats.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="bg-white px-6 py-7 relative overflow-hidden group"
            >
              <div className="absolute -bottom-12 -right-12 w-32 h-32 rounded-full bg-blue-100/40 blur-2xl group-hover:bg-blue-200/60 transition-colors" />
              <div className="relative">
                <div className="text-[34px] font-bold tracking-[-0.025em] text-slate-900 leading-none tabular-nums">{s.v}</div>
                <div className="mt-2 text-[13.5px] font-semibold text-slate-700">{s.l}</div>
                <div className="mt-0.5 text-[12.5px] text-slate-500">{s.s}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   COMPARISON
   ───────────────────────────────────────────────────────────────────────── */

const compareRows = [
  { label: 'Purpose-built dental AI model (ARIA)', asha: true, chatbots: false, pms: false },
  { label: 'Autonomous multi-step reasoning', asha: true, chatbots: false, pms: false },
  { label: 'Takes action — not just answers', asha: true, chatbots: false, pms: false },
  { label: '20+ AI employees ship on day one', asha: true, chatbots: 'one-agent', pms: false },
  { label: 'Layer onto Dentrix / Eaglesoft / Open Dental', asha: true, chatbots: 'partial', pms: false },
  { label: 'Replace PMS when you\'re ready', asha: true, chatbots: false, pms: false },
  { label: 'AI-native VoIP, SMS, fax included', asha: true, chatbots: false, pms: 'addon' },
  { label: 'Built in-house — not a ChatGPT wrapper', asha: true, chatbots: false, pms: false },
  { label: 'Per-seat fees + add-on tax', asha: false, chatbots: true, pms: true },
];

const ComparisonSection = () => (
  <section className="relative bg-slate-50/60 py-28">
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute top-1/2 right-1/3 w-[400px] h-[400px] bg-blue-100/40 rounded-full blur-[120px]" />
    </div>
    <div className="relative max-w-6xl mx-auto px-6">
      <div className="text-center max-w-3xl mx-auto mb-14">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-600 text-[10.5px] font-semibold uppercase tracking-wider mb-5 shadow-sm">
          <GitBranch className="w-3 h-3" /> differentiation
        </div>
        <h2 className="text-[34px] md:text-[48px] font-semibold tracking-[-0.025em] leading-[1.05] text-slate-900">
          Every other dental AI is a chatbot.
        </h2>
        <p className="mt-5 text-[16.5px] text-slate-600 leading-relaxed">
          They wrap ChatGPT. We built an operating system. Here's what the difference looks like.
        </p>
      </div>

      <div className="relative">
        <div className="absolute -inset-px bg-gradient-to-br from-blue-300/30 via-indigo-300/20 to-blue-300/30 rounded-2xl blur" />
        <div className="relative rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-md">
          {/* Header */}
          <div className="grid grid-cols-12 px-5 py-4 border-b border-slate-200 bg-slate-50/70 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
            <div className="col-span-6" />
            <div className="col-span-2 text-center">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-100">
                <Sparkles className="w-2.5 h-2.5 text-blue-600" />Asha
              </span>
            </div>
            <div className="col-span-2 text-center">AI chatbots</div>
            <div className="col-span-2 text-center">Legacy PMS</div>
          </div>
          {compareRows.map((r, i) => (
            <div key={r.label} className={`grid grid-cols-12 px-5 py-3.5 items-center text-[13.5px] ${i !== compareRows.length - 1 ? 'border-b border-slate-100' : ''}`}>
              <div className="col-span-6 text-slate-800 font-medium">{r.label}</div>
              {[r.asha, r.chatbots, r.pms].map((v, j) => (
                <div key={j} className="col-span-2 flex justify-center">
                  {v === true ? (
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${j === 0 ? 'bg-emerald-50 ring-1 ring-emerald-200' : 'bg-slate-100'}`}>
                      <Check className={`w-3.5 h-3.5 ${j === 0 ? 'text-emerald-600' : 'text-slate-400'}`} />
                    </div>
                  ) : v === false ? (
                    <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center">
                      <X className="w-3.5 h-3.5 text-slate-400" />
                    </div>
                  ) : (
                    <span className="text-[11px] font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded-full border border-amber-100">{v}</span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ─────────────────────────────────────────────────────────────────────────
   SECURITY/INFRA
   ───────────────────────────────────────────────────────────────────────── */

const SecuritySection = () => {
  const items = [
    { i: Lock, t: 'HIPAA + SOC2 Type II', s: 'AWS hosted. BAAs signed. Encrypted at rest + in transit.' },
    { i: RefreshCw, t: 'ARIA improves every day', s: 'Each action makes the model smarter — every practice benefits.' },
    { i: Globe, t: 'Cloud-native, no server', s: 'Skip the $15K refresh. Skip the IT contract.' },
    { i: Layers, t: 'Scales from 1 op to 50', s: 'Add a chair — ARIA picks up the extra calls. No hiring.' },
    { i: Clock, t: '4-week white-glove go-live', s: 'Migration, QA, staff training. Phased launch.' },
    { i: BarChart3, t: 'Ask-Asha analytics', s: 'Natural-language reporting. KPIs vs your production goals.' },
  ];
  return (
    <section className="relative bg-white py-28">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 text-[10.5px] font-semibold uppercase tracking-wider mb-5">
            <Shield className="w-3 h-3" /> security · infrastructure
          </div>
          <h2 className="text-[34px] md:text-[48px] font-semibold tracking-[-0.025em] leading-[1.05] text-slate-900">
            Enterprise-grade.
            <br />
            <span className="text-slate-400">Built for dentistry.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((b, i) => {
            const I = b.i;
            return (
              <motion.div
                key={b.t}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ delay: i * 0.05 }}
                className="group rounded-2xl bg-white border border-slate-200 p-5 hover:border-blue-200 hover:shadow-md transition-all shadow-sm"
              >
                <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center mb-4 group-hover:bg-blue-100 transition-colors">
                  <I className="w-4 h-4 text-blue-600" strokeWidth={1.75} />
                </div>
                <h3 className="text-[15.5px] font-semibold text-slate-900 tracking-tight">{b.t}</h3>
                <p className="mt-1.5 text-[13px] text-slate-600 leading-relaxed">{b.s}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   PRICING — single SaaS pricing card
   ───────────────────────────────────────────────────────────────────────── */

const PricingSection = () => (
  <section id="pricing" className="relative bg-slate-50/60 py-28 overflow-hidden">
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-blue-100/50 rounded-full blur-[140px]" />
    </div>
    <div className="relative max-w-5xl mx-auto px-6">
      <div className="text-center max-w-3xl mx-auto mb-12">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-600 text-[10.5px] font-semibold uppercase tracking-wider mb-5 shadow-sm">
          <DollarSign className="w-3 h-3" /> pricing
        </div>
        <h2 className="text-[34px] md:text-[48px] font-semibold tracking-[-0.025em] leading-[1.05] text-slate-900">
          One platform. One price.
        </h2>
        <p className="mt-4 text-[16px] text-slate-600 leading-relaxed">
          No per-seat fees. No per-module upsells. Net-positive month one for the average practice.
        </p>
      </div>

      <div className="relative">
        <div className="absolute -inset-px bg-gradient-to-br from-blue-300/50 via-indigo-300/30 to-blue-300/50 rounded-3xl blur opacity-70" />
        <div className="relative rounded-3xl border border-slate-200 bg-white shadow-xl shadow-blue-600/5 p-8 md:p-10">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-blue-50 border border-blue-100 text-blue-700 text-[10px] font-semibold uppercase tracking-wider mb-4">
                everything included
              </div>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-[56px] md:text-[64px] font-bold text-slate-900 tracking-[-0.03em] leading-none tabular-nums">$799</span>
                <span className="text-[18px] text-slate-500 font-medium">/mo</span>
              </div>
              <div className="text-[15px] text-slate-600">+ 3.5% of collections · flat</div>
              <div className="mt-6 text-[13.5px] text-slate-500">
                Replaces $4,200+ in monthly software cost
              </div>
              <Link
                to="/book-demo"
                className="mt-8 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-blue-600 text-white font-semibold text-[14.5px] hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/25"
              >
                Book your demo
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
                {[
                  '20+ AI employees',
                  'ARIA-2 reasoning model',
                  'Asha PMS (cloud)',
                  'PMS bridges (Dentrix etc.)',
                  'AI-native VoIP + SMS',
                  'Intelligent fax',
                  'Marketing suite',
                  'Cloud imaging (DICOM)',
                  'Insurance verification',
                  'eClaims + scrubbing',
                  '150K AI credits / mo',
                  'Unlimited users + calls',
                  'White-glove onboarding',
                  '24/7 support',
                ].map(f => (
                  <div key={f} className="flex items-center gap-2 text-[13px] text-slate-700">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    {f}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ─────────────────────────────────────────────────────────────────────────
   BIG CTA
   ───────────────────────────────────────────────────────────────────────── */

const BigCTA = () => (
  <section className="relative bg-white pb-24 overflow-hidden">
    <div className="max-w-7xl mx-auto px-6">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 p-10 md:p-16 shadow-2xl shadow-blue-600/30">
        <div className="absolute inset-0 opacity-40 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-400/30 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-400/30 rounded-full blur-3xl" />
          <div
            className="absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255, 255, 255, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.3) 1px, transparent 1px)',
              backgroundSize: '32px 32px',
            }}
          />
        </div>
        <div className="relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 border border-white/20 text-white text-[10.5px] font-semibold uppercase tracking-wider mb-5 backdrop-blur-sm">
              <Sparkles className="w-3 h-3" /> 2026 cohort · limited
            </div>
            <h2 className="text-[36px] md:text-[52px] font-semibold tracking-[-0.025em] leading-[1.02] text-white">
              See your AI workforce
              <br />
              <span className="text-blue-100/80">running on your practice.</span>
            </h2>
            <p className="mt-5 text-[17px] text-blue-50/90 max-w-xl leading-relaxed">
              20-minute demo. We'll route your top workflows through ARIA live and quote your exact ROI. No slideshow.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <Link to="/book-demo" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-white text-blue-700 font-semibold text-[15px] hover:bg-blue-50 transition-all shadow-xl">
              Book your demo
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/roi-calculator" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-white/10 text-white font-semibold text-[15px] hover:bg-white/15 transition-all border border-white/20 backdrop-blur-sm">
              Calculate your ROI
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ─────────────────────────────────────────────────────────────────────────
   PAGE
   ───────────────────────────────────────────────────────────────────────── */

const AlternateLanding2 = () => {
  return (
    <div className="min-h-screen bg-white">
      <FuturisticHeader />
      <HeroFuturistic />
      <AgentNetworkSection />
      <TwoPathsSection />
      <WorkforceSection />
      <StatsBar />
      <ComparisonSection />
      <SecuritySection />
      <PricingSection />
      <BigCTA />
      <Footer />
    </div>
  );
};

export default AlternateLanding2;
